import kebabCase from 'lodash/kebabCase';
import React, { MouseEventHandler, RefObject, useReducer, useRef } from 'react';
import colors from '../../../helpers/colors';
import componentProps from '../../../helpers/componentProps';
import { scrollToElement } from '../../../helpers/scroll';
import { useSticky } from '../../../hooks/useSticky';
import { applyComponentTheme } from '../../../theme';
import Collapsible from '../../Collapsible';
import { InPageNavProps } from '../types';
import { useActiveSection } from '../useActiveSection';
import NavItemMobile from './NavItemMobile';
import {
  InPageNavContainer,
  NavButton,
  NavDropdown,
  NavHeading,
  StyledIcon,
} from './styles';

const InPageNavMobile: React.FC<InPageNavProps> = ({
  navHeading,
  navSections,
  ...props
}) => {
  const sectionIds = navSections.map((section) => section.id);
  const navRef = useRef<HTMLDivElement>(null);
  useSticky(navRef);
  const activeSectionId = useActiveSection(sectionIds, navRef, 'bottom');
  const [displaySections, toggleDisplaySections] = useReducer((state) => !state, false);
  const navButtonId = `${kebabCase(navHeading)}-collapsible-nav-button`;
  const navItemsId = `${kebabCase(navHeading)}-collapsible-nav-items`;
  const onNavItemClick = (
    id: string,
    navItemRef: RefObject<HTMLDivElement>
  ): MouseEventHandler => (event) => {
    event.preventDefault();
    const inPageNavHeight = navItemRef?.current?.getBoundingClientRect().height ?? 0;
    scrollToElement(id, inPageNavHeight);
    toggleDisplaySections();
  };

  return (
    <InPageNavContainer ref={navRef} {...componentProps(props)}>
      <nav aria-label={`${navHeading} mobile in-page nav`}>
        <NavButton
          id={navButtonId}
          onClick={toggleDisplaySections}
          aria-expanded={displaySections}
          aria-controls={navItemsId}>
          <NavHeading>{navHeading}</NavHeading>
          <StyledIcon
            name={displaySections ? 'minus' : 'plus'}
            size="small"
            color={colors.core01}
          />
        </NavButton>
        <NavDropdown>
          <Collapsible
            isOpened={displaySections}
            id={navItemsId}
            role="region"
            aria-labelledby={navButtonId}>
            {navSections.map(({ text, id }) => (
              <NavItemMobile
                key={id}
                onClick={onNavItemClick(id, navRef)}
                href={encodeURI(`#${id}`)}
                active={activeSectionId === id}>
                {text}
              </NavItemMobile>
            ))}
          </Collapsible>
        </NavDropdown>
      </nav>
    </InPageNavContainer>
  );
};

export default applyComponentTheme(InPageNavMobile, 'InPageNavMobile');
